import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { ssCodeBare } from '../utils/unitIdFormat';
import { asList } from '../utils/asList';
import {
  catalogSubNumber,
  isDefaultCategory,
  maxCatalogSubNumber,
} from '../utils/equipmentTypeCatalog';

/**
 * Register numbered sub-types on one SS instance.
 * Catalog types use fixed numbers (Default=1). Units are always {prefix}-{n}.
 */
export default function SsEquipmentTypePool({ buildingId, instanceId, ssCode, compact = false }) {
  const queryClient = useQueryClient();
  const subNumberRef = useRef(null);
  const [subNumber, setSubNumber] = useState('1');
  const [pickedKey, setPickedKey] = useState('');
  const [customName, setCustomName] = useState('');

  const readSubNumber = () => {
    const raw = subNumberRef.current?.value ?? subNumber;
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n >= 1 ? n : 0;
  };

  const { data: types = [] } = useQuery({
    queryKey: ['ss-equipment-types', buildingId, instanceId],
    queryFn: async () => {
      const res = await api.get(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types`
      );
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!buildingId && !!instanceId,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['checklist-categories', ssCode],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/categories', { params: { ssCode } });
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!ssCode,
  });

  const usedKeys = useMemo(
    () => new Set((types || []).map((t) => String(t.category_key || '').toLowerCase())),
    [types]
  );
  const usedNumbers = useMemo(
    () => new Set((types || []).map((t) => Number(t.sub_number))),
    [types]
  );

  const poolButtons = useMemo(() => {
    const list = [];
    const cats = [...(categories || [])];
    cats.sort((a, b) => {
      const ad = isDefaultCategory(a) ? 0 : 1;
      const bd = isDefaultCategory(b) ? 0 : 1;
      if (ad !== bd) return ad - bd;
      return String(a.label || '').localeCompare(String(b.label || ''));
    });
    for (const c of cats) {
      const key = c.category_key || c.label;
      list.push({
        key,
        label: isDefaultCategory(c) ? 'Default' : c.label,
        description: c.description || '',
        catalogNumber: catalogSubNumber(c, categories),
      });
    }
    return list;
  }, [categories]);

  const invalidate = () => {
    queryClient.invalidateQueries(['ss-equipment-types', buildingId, instanceId]);
    queryClient.invalidateQueries(['building', buildingId, 'ss-instances']);
    queryClient.invalidateQueries(['ss-instance', buildingId, instanceId]);
  };

  const saveMutation = useMutation({
    mutationFn: async ({ label, category_key, is_custom, sub_number }) => {
      const n = parseInt(sub_number, 10);
      if (!n || n < 1) throw new Error('Enter a sub-number (1 or higher)');
      const res = await api.post(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types`,
        { sub_number: n, label, category_key, is_custom: !!is_custom }
      );
      return res.data?.data;
    },
    onSuccess: (row) => {
      toast.success(`Registered ${row?.display || row?.label}`);
      const taken = new Set([...(types || []).map((t) => Number(t.sub_number)), Number(row?.sub_number)]);
      let next = 1;
      while (taken.has(next)) next += 1;
      setSubNumber(String(next));
      setPickedKey('');
      setCustomName('');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Could not register type'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (typeId) => {
      await api.delete(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types/${typeId}`
      );
    },
    onSuccess: () => {
      toast.success('Removed type');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Cannot remove this type'),
  });

  const bare = ssCodeBare(ssCode);
  const preview = parseInt(subNumber, 10);
  const previewId = bare && preview > 0 ? `${bare}-${preview}` : '';

  const registerPicked = (cat) => {
    const n = cat.catalogNumber || catalogSubNumber({ category_key: cat.key, label: cat.label }, categories);
    if (!n) {
      toast.error('Could not resolve catalog number for this type');
      return;
    }
    if (usedNumbers.has(n)) {
      toast.error(`${bare ? `${bare}-${n}` : n} is already used on this SS`);
      return;
    }
    saveMutation.mutate({
      label: cat.label,
      category_key: cat.key,
      is_custom: false,
      sub_number: n,
    });
  };

  const registerCustom = () => {
    const label = customName.trim();
    if (!label) {
      toast.error('Enter a custom type name');
      return;
    }
    const catalogMax = maxCatalogSubNumber(categories) || 1;
    let n = readSubNumber();
    if (!n || n <= catalogMax) {
      n = catalogMax + 1;
      while (usedNumbers.has(n)) n += 1;
    }
    if (usedNumbers.has(n)) {
      toast.error(`${bare ? `${bare}-${n}` : n} is already used on this SS`);
      return;
    }
    saveMutation.mutate({
      label,
      category_key: label,
      is_custom: true,
      sub_number: n,
    });
  };

  return (
    <div className={`space-y-3 ${compact ? '' : 'rounded-xl border border-slate-200 bg-white p-4'}`}>
      {!compact && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Equipment types</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Catalog types have fixed numbers (Default ={' '}
            <span className="font-mono">{bare || '9'}-1</span>). Units are always{' '}
            <span className="font-mono">{bare || '9'}-1-1</span>,{' '}
            <span className="font-mono">{bare || '9'}-1-2</span>… Sub-number below is for custom
            types only.
          </p>
        </div>
      )}

      {types.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {types.map((t) => (
            <li
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs"
            >
              <span className="font-mono font-semibold text-primary-800">{t.prefix}</span>
              <span className="text-gray-800">{t.label}</span>
              <button
                type="button"
                className="p-0.5 text-red-500 hover:text-red-700"
                title="Remove unused type"
                onClick={() => deleteMutation.mutate(t.id)}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-gray-600">
          Custom sub-number
          <input
            ref={subNumberRef}
            type="number"
            min={1}
            value={subNumber}
            onChange={(e) => setSubNumber(e.target.value)}
            className="mt-0.5 block w-20 px-2 py-1.5 border rounded-lg font-mono text-sm"
          />
        </label>
        {previewId ? (
          <p className="text-xs text-primary-800 font-mono pb-2">→ {previewId}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {poolButtons.map((cat) => {
          const used = usedKeys.has(String(cat.key).toLowerCase());
          const registered = types.find(
            (t) => String(t.category_key).toLowerCase() === String(cat.key).toLowerCase()
          );
          const selected = pickedKey === cat.key;
          return (
            <button
              key={cat.key}
              type="button"
              disabled={used || saveMutation.isPending}
              title={used ? `Already ${registered?.prefix}` : cat.description || cat.label}
              onClick={() => {
                setPickedKey(cat.key);
                registerPicked(cat);
              }}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
                used
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                  : selected
                    ? 'bg-primary-600 text-white border-primary-700'
                    : 'bg-white text-slate-800 border-slate-300 hover:bg-primary-50 hover:border-primary-400'
              }`}
            >
              {used && registered ? (
                <span className="font-mono mr-1">{registered.prefix}</span>
              ) : cat.catalogNumber ? (
                <span className="font-mono mr-1 opacity-70">
                  {bare}-{cat.catalogNumber}
                </span>
              ) : null}
              {cat.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          className="flex-1 min-w-[10rem] px-2.5 py-1.5 border rounded-lg text-sm"
          placeholder="Custom type name"
        />
        <button
          type="button"
          disabled={saveMutation.isPending}
          onClick={registerCustom}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" />
          Save custom
        </button>
      </div>
    </div>
  );
}
